import { useEffect, useRef, useState } from "react";
import { ArrowUpRight, Loader2, Paperclip, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { API, http, formatApiError } from "../../lib/api";

const EMPTY = { name: "", email: "", phone: "", vertical: "", role: "" };

const Field = ({ label, children }) => (
  <label className="block">
    <span className="font-mono text-[11px] uppercase tracking-[0.18em] text-zinc-400">{label}</span>
    {children}
  </label>
);

const RolePicker = ({ roles, value, onChange }) => (
  <div className="flex flex-wrap gap-2 pt-3" data-testid="apply-role-options">
    {roles.map((r) => (
      <button
        type="button"
        key={r}
        onClick={() => onChange(r)}
        className={`rounded-full border px-3.5 py-1.5 text-sm transition-colors ${value === r ? "border-emerald-400 bg-emerald-500 text-[#0a0c0e]" : "border-white/15 text-zinc-200 hover:border-emerald-400/60"}`}
        data-testid={`apply-role-${r.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
      >
        {r}
      </button>
    ))}
  </div>
);

export const ApplyForm = ({ jobCode = "", testid = "apply-form" }) => {
  const [tax, setTax] = useState([]);
  const [f, setF] = useState(EMPTY);
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(null);
  const fileRef = useRef(null);

  useEffect(() => {
    http.get(`${API}/careers/taxonomy`)
      .then((r) => setTax(Array.isArray(r.data?.verticals) ? r.data.verticals : []))
      .catch(() => setTax([]));
  }, []);

  const roles = tax.find((v) => v.name === f.vertical)?.roles ?? [];
  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value, ...(k === "vertical" ? { role: "" } : {}) }));

  const submit = async (e) => {
    e.preventDefault();
    if (!f.vertical || !f.role) return toast.error("Please choose your industry vertical and job role.");
    if (!file) return toast.error("Please attach your CV (PDF or Word).");
    const fd = new FormData();
    Object.entries({ ...f, job_code: jobCode }).forEach(([k, v]) => fd.append(k, v));
    fd.append("cv", file);
    setLoading(true);
    try {
      const { data } = await http.post(`${API}/careers/apply`, fd);
      setDone(data?.reference ?? "");
      setF(EMPTY);
      setFile(null);
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  if (done !== null) {
    return (
      <div className="flex flex-col items-start py-6" data-testid={`${testid}-success`}>
        <CheckCircle2 className="h-11 w-11 text-emerald-400" strokeWidth={1.3} />
        <p className="font-display mt-5 text-4xl leading-none">Thank you.</p>
        <p className="mt-4 max-w-md text-zinc-300">Your CV is with our search team. A confirmation email is on its way to you.</p>
        <p className="mt-4 font-mono text-sm text-emerald-300" data-testid={`${testid}-reference`}>Reference: {done}</p>
        <button onClick={() => setDone(null)} className="mt-8 text-sm text-emerald-300 underline underline-offset-4" data-testid={`${testid}-again-btn`}>Submit another CV</button>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="grid grid-cols-1 gap-x-8 gap-y-7 sm:grid-cols-2" data-testid={testid}>
      <Field label="Full name *"><input required className="field" value={f.name} onChange={set("name")} placeholder="Your full name" data-testid={`${testid}-name`} /></Field>
      <Field label="Email *"><input required type="email" className="field" value={f.email} onChange={set("email")} placeholder="name@email.com" data-testid={`${testid}-email`} /></Field>
      <Field label="Phone *"><input required inputMode="tel" pattern="[+]?[0-9 ()-]{10,18}" title="10 to 13 digits" className="field" value={f.phone} onChange={set("phone")} placeholder="+91 98XXX XXXXX" data-testid={`${testid}-phone`} /></Field>
      <Field label="Industry vertical *">
        <select required className="field" value={f.vertical} onChange={set("vertical")} data-testid={`${testid}-vertical`}>
          <option value="" disabled>Select vertical</option>
          {tax.map((v) => <option key={v.name} value={v.name}>{v.name}</option>)}
        </select>
      </Field>
      {roles.length > 0 && (
        <div className="sm:col-span-2">
          <span className="font-mono text-[11px] uppercase tracking-[0.18em] text-zinc-400">Job role in {f.vertical} *</span>
          <RolePicker roles={roles} value={f.role} onChange={(r) => setF((p) => ({ ...p, role: r }))} />
        </div>
      )}
      <div className="sm:col-span-2">
        <span className="font-mono text-[11px] uppercase tracking-[0.18em] text-zinc-400">CV (PDF or Word, max 5 MB) *</span>
        <button type="button" onClick={() => fileRef.current?.click()} className="mt-3 flex w-full items-center gap-3 rounded-md border border-dashed border-white/20 px-4 py-4 text-left text-sm text-zinc-300 hover:border-emerald-400/60 transition-colors" data-testid={`${testid}-file-btn`}>
          <Paperclip className="h-4 w-4 text-emerald-400" />
          <span className="truncate">{file ? file.name : "Choose your CV file"}</span>
        </button>
        <input ref={fileRef} type="file" accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document" className="hidden" onChange={(e) => setFile(e.target.files?.[0] ?? null)} data-testid={`${testid}-file-input`} />
      </div>
      <div className="sm:col-span-2 flex flex-col-reverse gap-5 sm:flex-row sm:items-center sm:justify-between">
        <p className="text-xs text-zinc-400 max-w-xs">Your details are kept confidential and shared only for relevant mandates.</p>
        <button type="submit" disabled={loading} className="group inline-flex shrink-0 items-center justify-center gap-3 whitespace-nowrap rounded-full bg-emerald-500 pl-6 pr-2 py-2 text-[15px] font-medium text-[#0a0c0e] hover:bg-emerald-400 disabled:opacity-60 transition-colors" data-testid={`${testid}-submit`}>
          {loading ? "Sending" : jobCode ? "Submit application" : "Place CV on record"}
          <span className="grid h-10 w-10 place-items-center rounded-full bg-[#0a0c0e] text-emerald-400 transition-transform duration-500 group-hover:rotate-45">
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <ArrowUpRight className="h-4 w-4" />}
          </span>
        </button>
      </div>
    </form>
  );
};
